import { useState } from 'react';
import { Alert, Button, Stack, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { ApiFallo } from '../lib/api.js';

interface Props {
  downloadId: string;
  season: number | null;
  episode: number | null;
  episodeEnd: number | null;
  /** Tras guardar: la lista de conversiones tiene que releerse para que el nombre cambie. */
  onGuardado: () => void;
  onCancelar?: () => void;
}

const numero = (v: string): number | null => {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 ? n : null;
};

/** Temporada y episodio a mano para un fichero de una serie (D-27).
 *
 *  Es la salida para lo que el sitio no numeró —`El Infiltrado Episodio 1.mkv`— o numeró
 *  mal: sin temporada y número no hay forma de nombrarlo para Plex, y el fichero se queda
 *  esperando en conversiones. Lo que se guarda manda sobre lo que dedujo el parser y
 *  sobrevive a que se vuelva a leer la ficha. Vaciar los dos campos vuelve a lo deducido. */
export function EditorEpisodio({ downloadId, season, episode, episodeEnd, onGuardado, onCancelar }: Props) {
  const [temporada, setTemporada] = useState(season == null ? '' : String(season));
  const [episodio, setEpisodio] = useState(episode == null ? '' : String(episode));
  const [hasta, setHasta] = useState(episodeEnd == null ? '' : String(episodeEnd));

  const guardar = useMutation({
    mutationFn: () =>
      api.fijarEpisodio(downloadId, {
        season: numero(temporada),
        episode: numero(episodio),
        episodeEnd: numero(hasta),
      }),
    onSuccess: onGuardado,
  });

  const invalido =
    (temporada.trim() !== '' && numero(temporada) == null) ||
    (episodio.trim() !== '' && numero(episodio) == null) ||
    (hasta.trim() !== '' && (numero(hasta) == null || numero(episodio) == null || numero(hasta)! <= numero(episodio)!));

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <TextField
          size="small" label="Temporada" value={temporada} onChange={(e) => setTemporada(e.target.value)}
          slotProps={{ htmlInput: { inputMode: 'numeric', 'aria-label': 'Temporada' } }} sx={{ width: 110 }}
        />
        <TextField
          size="small" label="Episodio" value={episodio} onChange={(e) => setEpisodio(e.target.value)}
          slotProps={{ htmlInput: { inputMode: 'numeric', 'aria-label': 'Episodio' } }} sx={{ width: 110 }}
        />
        <TextField
          size="small" label="Hasta" value={hasta} onChange={(e) => setHasta(e.target.value)}
          placeholder="—"
          helperText=""
          slotProps={{ htmlInput: { inputMode: 'numeric', 'aria-label': 'Último episodio si el fichero trae varios' } }}
          sx={{ width: 90 }}
        />
        <Button variant="contained" size="small" disabled={invalido || guardar.isPending} onClick={() => guardar.mutate()}>
          {guardar.isPending ? 'Guardando…' : 'Guardar'}
        </Button>
        {onCancelar && (
          <Button size="small" onClick={onCancelar} disabled={guardar.isPending}>Cancelar</Button>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Los especiales son la temporada 0. «Hasta» solo si el fichero trae varios episodios seguidos
        (`s01e01-e02`). Vaciar los dos primeros vuelve a lo que dedujo el nombre del fichero.
      </Typography>
      {guardar.isError && (
        <Alert severity="error" onClose={() => guardar.reset()}>{(guardar.error as ApiFallo).message}</Alert>
      )}
    </Stack>
  );
}
